import { useEffect, useState } from 'react';

import { useOrganizationsQuery } from '../../../types/api.graphql';
import { DropdownItemProps } from '../OrganizationsDropdown';

const PAGE_ITEMS_COUNT = 10;

export const useOrganizationsDropdown = (initialIndex, onSelectionChange) => {
  const [items, setItems] = useState<DropdownItemProps[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(initialIndex);
  const [loadingMore, setLoadingMore] = useState(false);

  const {
    data,
    loading: loadingFirst,
    fetchMore,
  } = useOrganizationsQuery({
    variables: {
      first: PAGE_ITEMS_COUNT,
    },
    onCompleted: (dataOrgs) => {
      // Get existing organization IDs
      const existingIds = new Set(items.map((item) => item.value.id));
      // Filter out organizations that are already in the state
      const filteredOrgs =
        dataOrgs?.organizations?.nodes?.filter((organization) => !existingIds.has(organization.id)) || [];
      const newItems = [
        ...items,
        ...(filteredOrgs?.map((organization) => ({
          text: organization.name,
          imageUrl: organization.imageUrl,
          value: organization,
          key: organization.id,
        })) || []),
      ];
      setItems(newItems);
      setHasMore(dataOrgs?.organizations?.pageInfo?.hasNextPage);
    },
  });

  const handleLoadMore = async () => {
    setLoadingMore(true);
    await fetchMore({
      variables: {
        after: data?.organizations?.pageInfo?.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          organizations: {
            nodes: [...(prev.organizations?.nodes || []), ...(fetchMoreResult.organizations?.nodes || [])],
            pageInfo: fetchMoreResult.organizations?.pageInfo,
            totalCount: fetchMoreResult.organizations?.totalCount,
          },
        };
      },
    });
    setLoadingMore(false);
  };

  const handleItemSelection = (selection, updatedItems?: DropdownItemProps[]) => {
    const selectedIndex = selection as number;
    setSelectedIndex(selectedIndex);
    const itemsList = updatedItems ?? items;
    onSelectionChange?.(selectedIndex, itemsList[selectedIndex]?.value);
  };

  useEffect(() => {
    if (items?.length === 1) {
      handleItemSelection(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  return {
    items,
    hasMore,
    selectedIndex,
    loadingFirst,
    loadingMore,
    handleLoadMore,
    handleItemSelection,
  };
};
